/** 展示格式化。凡涉及金额的一律走字符串，见 formatCost 的注释。 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.locale('zh-cn');

/** 接口一律返回 RFC 3339 UTC，展示时转本地时区 */
export function formatTime(iso: string | null | undefined, template = 'YYYY-MM-DD HH:mm:ss'): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format(template) : '—';
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.fromNow() : '—';
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)} s`;
  const m = Math.floor(s / 60);
  const rest = Math.round(s % 60);
  if (m < 60) return `${m} 分 ${rest} 秒`;
  const h = Math.floor(m / 60);
  return `${h} 时 ${m % 60} 分`;
}

/**
 * 成本直接渲染服务端给的字符串。
 * 后端由 total_cost_micro / 1e6 格式化而来，parseFloat 再 toFixed 会二次引入浮点误差
 * （0.004521 这种六位小数正是二进制浮点表示不精确的区间），且没有任何收益。
 */
export function formatCost(cost: string | null | undefined, currency = '$'): string {
  if (cost === null || cost === undefined || cost === '') return '—';
  return `${currency}${cost}`;
}

/**
 * 十进制字符串求和。
 *
 * 概览页要把每天的 total_cost 加起来，但这些值是六位小数的金额 ——
 * 转成 Number 相加正是「0.1 + 0.2 !== 0.3」的场景，几百天累加后误差肉眼可见。
 * 这里按最长小数位对齐后用 BigInt 做整数加法，全程不经过浮点。
 */
export function sumDecimalStrings(values: Array<string | null | undefined>): string {
  const parsed = values
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => v.trim());
  if (parsed.length === 0) return '0';

  let scale = 0;
  for (const v of parsed) {
    const dot = v.indexOf('.');
    if (dot >= 0) scale = Math.max(scale, v.length - dot - 1);
  }

  let total = 0n;
  for (const v of parsed) {
    const neg = v.startsWith('-');
    const body = neg ? v.slice(1) : v;
    const [int = '0', frac = ''] = body.split('.');
    // 非数字字符直接跳过，避免一个脏值把整列变成 NaN
    if (!/^\d*$/.test(int) || !/^\d*$/.test(frac)) continue;
    const scaled = BigInt(`${int}${frac.padEnd(scale, '0')}` || '0');
    total += neg ? -scaled : scaled;
  }

  if (scale === 0) return total.toString();
  const neg = total < 0n;
  const digits = (neg ? -total : total).toString().padStart(scale + 1, '0');
  const int = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale);
  return `${neg ? '-' : ''}${int}.${frac}`;
}

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('zh-CN');
}

/** Token 数在图表轴与卡片上做紧凑显示 */
export function formatCompact(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (Math.abs(n) < 1000) return String(n);
  if (Math.abs(n) < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export function truncate(s: string, max = 80): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** JSON 预览：非法值也要能显示，不能抛 */
export function safeStringify(v: unknown, space = 2): string {
  try {
    return JSON.stringify(v, null, space) ?? String(v);
  } catch {
    return String(v);
  }
}
